import ActiveToggleButton from "@/components/ActiveToggleButton";
import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import StateMasterBadge from "@/components/StateMasterBadge";
import { WorkflowStateService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { StateMaster } from "@/utils/types/stateMasters";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAdminNavigate from "@/hooks/useAdminNavigate";

const StateMasterListPage = () => {
  const navigate = useAdminNavigate();

  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  // Use React Query for data fetching
  const {
    data: stateMasters,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["state-masters", page, pageSize],
    queryFn: () => WorkflowStateService.getAllStateMasters({ page, pageSize }),
  });

  // Use React Query for mutations

  const gridData = stateMasters?.results ?? [];
  const total = stateMasters?.count ?? 0;

  const handleDelete = async (id: string) => {
    const response = await WorkflowStateService.deleteStateMaster(id);
    if (response) {
      refetch();
    }
  };

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.STATE_MASTER_EDIT(id));
  };

  const columns: ProColumns<StateMaster>[] = [
    {
      title: "State Master Name",
      dataIndex: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Preview",
      dataIndex: "preview",
      key: "preview",
      render: (_, record) => <StateMasterBadge stateMaster={record} />,
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.workflow_state_master}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record.id)}
          type="text"
          title="Edit State"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this state?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete State"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<StateMaster>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(navigationRoutes.STATE_MASTER_ADD)}
          title="Add New State"
        >
          Add State
        </CustomButton>,
      ]}
      headerTitle="State Masters List"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size?.toString() ?? "10");
        navigate(
          `${navigationRoutes.STATE_MASTERS}?${searchParams.toString()}`
        );
      }}
      request={() => refetch()}
      tableName={gridName.stateMasterTable}
    />
  );
};

export default StateMasterListPage;
